import React from 'react';
import { clearAppState } from '../utils/authRecovery';
import './ErrorBoundary.css';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  copied: boolean;
}

/**
 * Last line of defence against a blank page.
 *
 * Mounted *outside* MsalProvider so it also catches a throw from the provider
 * itself, which means it must not use any MSAL hook.
 *
 * Note what this cannot do: a React error boundary only catches errors raised
 * during render, in lifecycles, and in effects of its children. It is a safety
 * net, not a fix for failures outside the React tree.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // No remote reporting by design: error text here can contain email
    // subjects and document content, and shipping that to a backend would
    // undo the PII decision made in utils/logger.ts. The user copies the
    // diagnostics onto the ticket instead.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private buildDiagnostics(): string {
    const { error, componentStack } = this.state;
    return [
      `when:      ${new Date().toISOString()}`,
      `url:       ${window.location.href}`,
      `agent:     ${navigator.userAgent}`,
      `error:     ${error?.name}: ${error?.message}`,
      '',
      'stack:',
      error?.stack ?? '(none)',
      '',
      'component stack:',
      componentStack ?? '(none)',
    ].join('\n');
  }

  private handleCopy = async (): Promise<void> => {
    const text = this.buildDiagnostics();
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
    } catch {
      // Clipboard is permission-gated. Select the pre block instead so the
      // user can copy by hand rather than being told it worked when it didn't.
      const pre = document.getElementById('eb-diagnostics');
      if (pre) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
    }
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleReset = (): void => {
    clearAppState();
    // Drop any auth fragment so the reloaded page starts clean.
    window.location.replace(window.location.origin + window.location.pathname);
  };

  render(): React.ReactNode {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="eb-screen" role="alert">
        <div className="eb-card">
          <h1>Something went wrong</h1>
          <p className="eb-lede">
            R&amp;R Realty AI hit an unexpected error and stopped loading. Reloading usually
            clears it. If it keeps happening, copy the diagnostics below onto your IT ticket.
          </p>

          <div className="eb-actions">
            <button type="button" className="eb-primary" onClick={this.handleReload}>
              Reload
            </button>
            <button type="button" className="eb-secondary" onClick={this.handleCopy}>
              {copied ? 'Diagnostics copied' : 'Copy diagnostics'}
            </button>
          </div>

          <details className="eb-details">
            <summary>Diagnostics</summary>
            <pre id="eb-diagnostics">{this.buildDiagnostics()}</pre>
          </details>

          <div className="eb-escalation">
            <p>
              Still broken after a reload? This clears saved chats and sign-in progress on this
              device. You stay signed in.
            </p>
            <button type="button" className="eb-danger" onClick={this.handleReset}>
              Reset app data and reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
